import React, {useRef} from 'react';
import './RecruitUnitCard.css';
import UNIT_TYPES from '../../../../configs/units';
import {UNIT_D4_MAX_SHOOTING_RANGE} from '../../../../constants/units';
import {getUnitIconClassNameForButton} from '../../../../utils/unit/getUnitIconClassNameForButton';
import {getPlayerColorStyle} from '../../../../utils/gameUi/getPlayerColorStyle';
import UnitIcon from '../UnitIcon/UnitIcon';
import {ADDITIONAL_SUPPLY_TOOLTIP, useUnitStatsData} from '../SelectedUnitStats/useUnitStatsData';
import {checkIfColorBlendsWithStone} from '../../../../utils/gameUi/checkIfColorBlendsWithStone';
import {MaskedIcon} from '../../../coloredIcons/MaskedIcon/MaskedIcon';
import {GAME_BUTTON_SIZES, GameButton} from '../../../gameUi/GameButton';
import {joinClassNames} from '../../../gameUi/joinClassNames';
import RollingValue from './RollingValue';
import {useCardLift} from './useCardLift';

// Шкалы характеристик — от самого большого значения среди типов отрядов:
// сравнивать типы между собой можно, просто ведя мышью по ряду жетонов
const ALL_UNIT_TYPES = Object.values(UNIT_TYPES);
const MAX_PRICE = Math.max(...ALL_UNIT_TYPES.map((unitType) => unitType.price));
const MAX_SIZE = Math.max(...ALL_UNIT_TYPES.map((unitType) => unitType.maxSize));
const MAX_SPEED = Math.max(...ALL_UNIT_TYPES.map((unitType) => unitType.speed));

const NO_RANGE_TEXT = '—';

// Значение размера катится по пределу, а при том же пределе — по самому размеру:
// докупка катит вверх, откат докупки вниз
const SIZE_RANK_STEP = 1000;

// Размер отряда великанов (99) шире шкалы других типов — упирается в ее конец
const toGaugeFill = (value, max) => Math.min(1, value / max);

const CardStat = ({modifier, title, icon, value, rank, fill, maxFill, segments, isMuted}) => (
  <div
    className={joinClassNames('recruit-card_stat', `recruit-card_stat_${modifier}`, isMuted && 'recruit-card_stat_muted')}
    title={title}
    style={{
      '--stat-fill': fill,
      ...(maxFill !== undefined ? {'--stat-max-fill': maxFill} : null),
      ...(segments ? {'--stat-segments': segments} : null),
    }}
  >
    <div className={'recruit-card_stat-head'}>
      {icon}
      <RollingValue value={value} rank={rank}/>
    </div>
    <div className={joinClassNames('recruit-card_gauge', segments && 'recruit-card_gauge_segmented')}>
      {maxFill !== undefined && <div className={'recruit-card_gauge-fill recruit-card_gauge-fill_max'}/>}
      <div className={'recruit-card_gauge-fill'}/>
    </div>
  </div>
);

const RecruitUnitCardContent = ({unit, isUnaffordable, isInspect}) => {
  const {
    unitIconProps,
    isExistingUnitSelected,
    handleUnitUpgrade,
    isUnitUpgradeAvailable,
    finalSizePrice,
    isSizeDiscount,
    shootingRange,
  } = useUnitStatsData(unit);

  const {type, description, size, maxSize, speed, additionalSupply} = unit;
  // Цена нанятого отряда — докупка +1 к размеру, остальных — найм
  const price = isExistingUnitSelected ? finalSizePrice : unit.price;
  const isDiscount = isExistingUnitSelected ? isSizeDiscount : unit.isDiscount;
  const isRangeShown = shootingRange !== null;
  // Белая обводка силуэта на каменном гексе — по цвету самого отряда: в панели
  // осмотра он бывает любого игрока
  const isOutlined = checkIfColorBlendsWithStone(unitIconProps.playerColor);

  return (
    <div className={'recruit-card_content'} style={getPlayerColorStyle(unitIconProps.playerColor)}>
      {/* Одноразовые вспышки при смене типа перезапускает key */}
      <span key={`sweep-${type}`} className={'recruit-card_sweep'} aria-hidden={true}/>

      <div className={'recruit-card_portrait'}>
        <span className={'recruit-card_portrait-glow'} aria-hidden={true}/>
        <span key={`ring-${type}`} className={'recruit-card_portrait-ring'} aria-hidden={true}/>
        <div key={type} className={'recruit-card_portrait-unit'}>
          <div className={isOutlined ? 'recruit-card_portrait-outline' : undefined}>
            <UnitIcon
              unitType={type}
              playerColor={unitIconProps.playerColor}
              factionId={unitIconProps.factionId}
              hasUnitSkin={unitIconProps.hasUnitSkin}
              useNeutralIcon={unitIconProps.useNeutralIcon}
              capturedOriginalColor={unitIconProps.capturedOriginalColor}
              className={getUnitIconClassNameForButton(type)}
            />
          </div>
        </div>
        {additionalSupply && (
          <img
            className={'recruit-card_supply-badge'}
            src={'/img/icons/supply outline.png'}
            alt={'Дополнительное снабжение'}
            title={ADDITIONAL_SUPPLY_TOOLTIP}
          />
        )}
      </div>

      <div className={'recruit-card_details'}>
        <div className={'recruit-card_title-row'}>
          <div key={type} className={'recruit-card_name'}>{description}</div>
          {isUnitUpgradeAvailable && (
            <GameButton
              size={GAME_BUTTON_SIZES.ICON}
              onClick={handleUnitUpgrade}
              className={'recruit-card_upgrade-button'}
              title={'Улучшение отряда'}
            >
              <img src={'/img/icons/upgrade.png'} alt={'Upgrade'}/>
            </GameButton>
          )}
        </div>

        <div className={joinClassNames('recruit-card_stats', isInspect && !isRangeShown && 'recruit-card_stats_no-range')}>
          <CardStat
            modifier={'price'}
            title={isExistingUnitSelected ? 'Цена найма +1 размера отряда' : 'Цена найма отряда'}
            icon={<>
              <img className={'recruit-card_stat-icon'} src={'/img/icons/coins.png'} alt={'price'}/>
              {isExistingUnitSelected && (
                <span className={'recruit-card_stat-size-plus'}>
                  +<MaskedIcon className={'recruit-card_stat-size-plus-icon'} src={'/img/icons/unit size.png'} alt={'unit size'}/>
                </span>
              )}
              {isDiscount && (
                <img className={'recruit-card_stat-discount'} src={'/img/icons/percent white.png'} alt={'Скидка'} title={'Скидка'}/>
              )}
            </>}
            value={price}
            rank={price}
            fill={toGaugeFill(price, MAX_PRICE)}
            isMuted={isUnaffordable}
          />
          <CardStat
            modifier={'size'}
            title={'Размер отряда и его предел'}
            icon={<MaskedIcon className={'recruit-card_stat-icon'} src={'/img/icons/unit size.png'} alt={'unit size'}/>}
            value={`${size}/${maxSize}`}
            rank={maxSize * SIZE_RANK_STEP + size}
            fill={toGaugeFill(size, MAX_SIZE)}
            maxFill={toGaugeFill(maxSize, MAX_SIZE)}
          />
          <CardStat
            modifier={'speed'}
            title={'Дальность перемещения'}
            icon={<MaskedIcon className={'recruit-card_stat-icon'} src={'/img/icons/speed.png'} alt={'Дальность перемещения'}/>}
            value={speed}
            rank={speed}
            fill={toGaugeFill(speed, MAX_SPEED)}
            segments={MAX_SPEED}
          />
          {/* В найме типы сравнивают между собой, и прочерк значим; при осмотре
              конкретного отряда стрельба, которой у него нет, — лишняя */}
          {(isRangeShown || !isInspect) && (
            <CardStat
              modifier={'range'}
              title={isRangeShown ? 'Дальность стрельбы' : 'Отряд не стреляет'}
              icon={<img className={'recruit-card_stat-icon'} src={'/img/icons/shooting.png'} alt={'Дальность стрельбы'}/>}
              value={isRangeShown ? shootingRange : NO_RANGE_TEXT}
              rank={isRangeShown ? shootingRange : 0}
              fill={isRangeShown ? toGaugeFill(shootingRange, UNIT_D4_MAX_SHOOTING_RANGE) : 0}
              segments={UNIT_D4_MAX_SHOOTING_RANGE}
              isMuted={!isRangeShown}
            />
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Карточка отряда в ряду найма. Раскрывается, когда есть что показать (выбранный или
 * подсвеченный мышью тип, отряд на поле), и складывается обратно, когда показывать
 * нечего. На десктопе — влево от жетонов: плита футера растет левым краем, правый край
 * стоит у колонки кнопки хода, и жетоны не уезжают из-под курсора (Footer.css). Вне
 * десктопа — вверх над рядом: футер стоит низом у края окна
 *
 * @param {object} [unit] - отряд для показа; пока складывается, виден прежний
 * @param {boolean} [isWing] - крыло панели осмотра ячейки: разделитель слева
 * @param {boolean} [isStacked] - вне десктопа: над рядом во всю ширину, раскрывается вверх
 * @param {boolean} [isUnaffordable] - цена больше монет игрока
 * @param {boolean} [isInspect] - панель осмотра ячейки: без стрельбы у нестреляющего отряда
 */
export const RecruitUnitCard = ({unit, isWing, isStacked, isUnaffordable, isInspect}) => {
  const lastUnitRef = useRef(unit);
  if (unit) {
    lastUnitRef.current = unit;
  }
  const shownUnit = lastUnitRef.current;
  const isOpen = Boolean(unit);
  const cardRef = useRef(null);
  const isClosing = useCardLift(cardRef, isOpen, Boolean(isStacked));

  return (
    <div
      ref={cardRef}
      className={joinClassNames(
        'recruit-card',
        (isOpen || isClosing) && 'recruit-card_open',
        isWing && 'recruit-card_wing',
        isStacked && 'recruit-card_stacked',
      )}
      aria-hidden={!isOpen}
    >
      {shownUnit && (
        <RecruitUnitCardContent unit={shownUnit} isUnaffordable={isUnaffordable} isInspect={isInspect}/>
      )}
      <span className={'recruit-card_divider'} aria-hidden={true}/>
    </div>
  );
};

export default RecruitUnitCard;
